import {
  ChangeEvent,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useInputPriceFocusContext } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { getTextFieldState } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useUserCanSetPrice } from 'src/hooks/useUserHasListingPermissions';
import {
  CheckIcon,
  CrossIcon,
  IconsFill,
  ProcessingIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { onPriceFieldChange } from 'src/utils/inventoryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  InventoryViewMode,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingMetrics,
  ListingQuery,
  ListingStatus,
} from 'src/WebApiController';

import { TableCellDiv } from './ListingPriceCell.styled';
import { ListingPricingInput, PriceField } from './ListingPriceForm.types';
import { useFieldIsSubmitting } from './useCellState';

type ListingPriceCellProps = {
  listing: Listing;
  currencyCode: string;
  nextEditableListingId?: number | undefined;
  sellerFee?: number | null;
  field: PriceField;
  otherField: PriceField;
  disabled?: boolean;
  onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
  viagVirtId?: string;
  prefixDisplay?: ReactNode;
  parentStyles?: string;
  isV2Variant?: boolean;
};

const ListingPriceCellRaw = ({
  listing,
  currencyCode,
  sellerFee,
  field,
  otherField,
  onFocus,
  disabled,
  viagVirtId,
  nextEditableListingId,
  prefixDisplay,
  parentStyles,
  isV2Variant = false,
}: ListingPriceCellProps) => {
  const { id: listingId, idOnMkp, status } = listing;
  const { getUiCurrency } = useLocalizationContext();

  const { setValue, watch, clearErrors, getFieldState, formState } =
    useFormContext<ListingPricingInput>();
  const { getSelection } = useMultiSelectionContext();
  const listingSelection = getSelection(viagVirtId);
  const isSelectionMode = listingSelection.isSingleGroupMultiSelect;

  const price = watch(field) ?? 0;
  const otherPrice = watch(otherField) ?? 0;

  const isSubmitting = watch('isSubmitting');
  const isSubmittingPricingSettings = watch('isSubmittingPricingSettings');
  const rowIndex = watch('rowIndex');
  const currentPageIndex = watch('currentPageIndex');

  const priceIsSubmitting = useFieldIsSubmitting();

  const uiCurrency = useMemo(
    () => getUiCurrency(currencyCode),
    [currencyCode, getUiCurrency]
  );
  const {
    pageSize,
    disablePagination,
    inputPriceFocusContext,
    setInputPriceFocusContext,
    disabledFocusContexts,
  } = useInputPriceFocusContext();

  const { filterQuery } = useFilterQueryContext<ListingQuery>();

  const fieldError = getFieldState(field, formState)?.error?.message;

  const priceHasChanged =
    formState.defaultValues?.[field] !== price &&
    price != null &&
    price >= 0 &&
    !isSubmitting;

  // ListingTable don't always have these context wrappers - remember not to assume these are NOT NULL
  const findNextFocusIndex = useCallback(() => {
    // need to skip the disabled rows from disabledFocusContexts
    let newRowIndex = rowIndex + 1;
    let nextRowIndex =
      !disablePagination && pageSize && newRowIndex >= pageSize
        ? 0
        : newRowIndex;
    let nextPageIndex = disablePagination
      ? 0
      : nextRowIndex === 0
        ? currentPageIndex + 1
        : currentPageIndex;

    while (
      disabledFocusContexts?.some(
        (x) =>
          x?.currentPageIndex === nextPageIndex &&
          x?.rowIndex === nextRowIndex &&
          (x?.viagVirtId === viagVirtId ||
            filterQuery.viewMode === InventoryViewMode.FlattenedView)
      )
    ) {
      newRowIndex = nextRowIndex + 1;
      nextRowIndex =
        !disablePagination && pageSize && newRowIndex >= pageSize
          ? 0
          : newRowIndex;
      nextPageIndex = disablePagination
        ? 0
        : nextRowIndex === 0
          ? nextPageIndex + 1
          : nextPageIndex;
    }

    return { nextRowIndex, nextPageIndex };
  }, [
    currentPageIndex,
    disablePagination,
    disabledFocusContexts,
    filterQuery.viewMode,
    pageSize,
    rowIndex,
    viagVirtId,
  ]);

  const inputIsFocus = useMemo(() => {
    if (!inputPriceFocusContext) {
      return false;
    }
    const {
      viagVirtId: ctxEventId,
      rowIndex: ctxRowIndex,
      listingId,
      priceField,
    } = inputPriceFocusContext;

    if (priceField !== field) {
      return false;
    }

    if (
      ctxEventId !== viagVirtId &&
      filterQuery.viewMode === InventoryViewMode.FlattenedView
    ) {
      return false;
    }

    // Listing id over rowIndex
    if (listingId) {
      return listingId === listing.id;
    }
    return ctxRowIndex === rowIndex;
  }, [
    viagVirtId,
    field,
    filterQuery.viewMode,
    inputPriceFocusContext,
    listing.id,
    rowIndex,
  ]);

  const saveMsg = useContent(ContentId.Save);
  const cancelMsg = useContent(ContentId.Cancel);

  const { showErrorDialog } = useClientLogContext();
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const { activeAccountWebClientConfig } = useAppContext();
  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();

  const isListed =
    status === ListingStatus.Listed || status === ListingStatus.ListingPending;

  const onBroadcastClick = useCallback(() => {
    tryInvokeApi(
      async () => {
        const client = new ListingClient(activeAccountWebClientConfig);

        const result = await client.deleteMarketplaceListings(listingId, []);
        if (result) {
          refreshMetrics?.();

          // Refresh the active data so the SaleDetail dialog and table will have the new content
          setActivePosEntity(listingId, idOnMkp, true, [
            ListingDetailDataField.Basic,
          ]);
        }
      },
      (error) => {
        showErrorDialog('ListingClient.deleteMarketplaceListings', error, {
          trackErrorData: { listingId, idOnMkp },
        });
      }
    );
  }, [
    activeAccountWebClientConfig,
    listingId,
    idOnMkp,
    refreshMetrics,
    setActivePosEntity,
    showErrorDialog,
  ]);

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value) ?? 0;
      onPriceFieldChange(
        newValue,
        field,
        price,
        otherField,
        otherPrice,
        // This is the otherToFieldRatio, so if field is allInPrice, the ratio os All-In-to-List
        field === 'allInPrice'
          ? 1 - (sellerFee ?? 0) // all-in-to-list
          : 1 / (1 - (sellerFee ?? 0)), // list-to-all-in
        clearErrors,
        setValue
      );
    },
    [clearErrors, field, otherField, otherPrice, price, setValue, sellerFee]
  );

  const onBlurHandler = useCallback(() => {
    if (
      rowIndex !== undefined &&
      inputPriceFocusContext?.rowIndex === rowIndex
    ) {
      setInputPriceFocusContext(undefined);
    }
  }, [rowIndex, inputPriceFocusContext?.rowIndex, setInputPriceFocusContext]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handlePriceSubmit = useCallback(() => {
    setValue('isSubmitting', true);

    if (price === 0 && otherPrice === 0 && isListed) {
      // If both price is zero and the listing is listed - ask
      // user if they want to unbroadcast
      onBroadcastClick();
    }
  }, [isListed, onBroadcastClick, otherPrice, price, setValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      if (fieldError) {
        return;
      }
      if (rowIndex !== undefined) {
        if (priceHasChanged) {
          e.stopPropagation();
          // Only submit if Enter is hit in the box that has changes
          handlePriceSubmit();
        } else {
          e.stopPropagation();
        }

        const { nextRowIndex, nextPageIndex } = findNextFocusIndex();
        setInputPriceFocusContext({
          viagVirtId: viagVirtId!,
          rowIndex: nextRowIndex,
          priceField: field,
          currentPageIndex: nextPageIndex,
          listingId: nextEditableListingId,
        });
      }
    }
  };

  useEffect(() => {
    if (inputIsFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
      inputRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [inputIsFocus]);

  const canSetPrice = useUserCanSetPrice(listing, false);

  const formFieldStyle = isV2Variant ? { height: '100%' } : undefined;

  const content = (
    <PosFormField
      errors={disabled ? undefined : fieldError}
      showErrorsInline
      style={formFieldStyle}
      fullHeight={isV2Variant}
    >
      <PosCurrencyField
        onClickCapture={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        rootProps={{
          variant: 'small',
          state: getTextFieldState(fieldError),
          disabled:
            disabled ||
            priceIsSubmitting ||
            isSelectionMode ||
            isSubmittingPricingSettings,
          className: parentStyles,
        }}
        uiCurrency={uiCurrency}
        alignment="right"
        ref={inputRef}
        disabled={
          disabled ||
          priceIsSubmitting ||
          isSubmittingPricingSettings ||
          isSelectionMode ||
          !canSetPrice
        }
        currencyFieldPrefixDisplay={prefixDisplay}
        postfixDisplay={
          !disabled && !fieldError && priceHasChanged ? (
            <>
              <CheckIcon
                title={saveMsg}
                withHoverEffect
                size={vars.iconSize.s}
                fill={IconsFill.textSuccess}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePriceSubmit();
                }}
              />
              <CrossIcon
                title={cancelMsg}
                withHoverEffect
                size={vars.iconSize.s}
                fill={IconsFill.textDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(field, formState.defaultValues?.[field] ?? 0);
                  setValue(
                    otherField,
                    formState.defaultValues?.[otherField] ?? 0
                  );
                  clearErrors(field);
                  clearErrors(otherField);
                  inputRef.current?.blur();
                }}
              />
            </>
          ) : priceIsSubmitting ? (
            <div className="operation-button">
              <RotatingWrapper>
                <ProcessingIcon size={vars.iconSize.l} />
              </RotatingWrapper>
            </div>
          ) : undefined
        }
        value={price}
        onBlur={onBlurHandler}
        onChange={onChangeHandler}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onClick={() => inputRef?.current?.select()}
      />
    </PosFormField>
  );

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const ListingPriceCell =
  memo<ListingPriceCellProps>(ListingPriceCellRaw);
